import { PsHttpService, type PatSnapSuccessResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { coreSearchApiUrlPrefix, accountServiceApiUrlPrefix } from 'library-@/config/constants';
import type {
    ApiSearchSettingLanguageRequest, ApiTranslateSpecificFieldRequest,SearchSettingLanguage,
    ApiSearchSettingLanguageResponse, ApiTranslateSpecificFieldResponse, TranslateSpecificField,
    ApiFetchCompareInitRequest, ApiFetchCompareInitResponse,FetchCompareInit,
    ApiGetCompareListRequest, ApiGetCompareListResponse, GetCompareList,
    ApiGetCompareOverviewRequest, ApiGetCompareOverviewResponse, GetCompareOverview,
    ApiGetCompareTextRequest, ApiGetCompareTextResponse, GetCompareText,
    ApiSearchComparePatentRequest, ApiSearchComparePatentResponse, SearchComparePatent,
    ApiAddComparePatentRequest, ApiAddComparePatentResponse, AddComparePatent,
    ApiGetComparePatentTextRequest, ApiGetComparePatentTextResponse, GetComparePatentText,
} from '../core-search-api';
import type { SearchSocketJob } from '@analytics/shared/apis/core-search-api-service.interface';
import type { ApiSearchSocketJobRequest, ApiSearchSocketJobResponse } from '@analytics/shared/apis/core-search-api-service.interface';
import type { ApiGetPatentInitTabRequest, ApiGetPatentInitTabResponse, GetPatentInitTab } from '../get-patent-init-tab-api';
import type { ApiGetPatentBasicRequest, ApiGetPatentBasicResponse, GetPatentBasic } from '../get-patent-basic-api';
import type { ApiGetPatentValuationRequest, ApiGetPatentValuationResponse, GetPatentValuation } from '../get-patent-valuation-api';
import type { ApiGetPatentViewInitRequest, ApiGetPatentViewInitResponse, GetPatentViewInit } from '../patent-view-init-api';
import type { ApiGetClaimFeatureRequest, ApiGetClaimFeatureResponse, GetClaimFeature } from '../get-claim-feature-api';
import type { ApiGetClaimCompareMatchRequest, ApiGetClaimCompareMatchResponse, GetClaimCompareMatch } from '../get-claim-compare-match-api';
import type { ApiGetClaimCompareTranslationRequest, ApiGetClaimCompareTranslationResponse, GetClaimCompareTranslation } from '../get-claim-compare-translation-api';
import type { ApiSetClaimCompareTranslationCacheRequest, ApiSetClaimCompareTranslationCacheResponse, SetClaimCompareTranslationCache } from '../get-claim-compare-translation-cache-api';
import { FactoryPsHttpSSE, type PsHttpSSE } from '@patsnap-utils/sse';
import psHttp from '@patsnap/common-ps-http';
import type { ApiGetClaimCompareResultRequest, ApiGetClaimCompareResultResponse, GetClaimCompareResult } from '../get-claim-compare-result-api';
import type { ApiGetPatentPumRequest, ApiGetPatentPumResponse, GetPatentPum } from '../get-patent-pum-api';

@injectable()
export class CoreSearchApiServiceImpl extends PsHttpService
    implements
        TranslateSpecificField,
        SearchSettingLanguage,
        FetchCompareInit,
        GetCompareList,
        GetCompareOverview,
        GetCompareText,
        SearchComparePatent,
        AddComparePatent,
        SearchSocketJob,
        GetComparePatentText,
        GetPatentInitTab,
        GetPatentBasic,
        GetPatentValuation,
        GetPatentViewInit,
        GetClaimFeature,
        GetClaimCompareMatch,
        GetClaimCompareTranslation,
        SetClaimCompareTranslationCache,
        GetClaimCompareResult,
        GetPatentPum
{
    @inject(FactoryPsHttpSSE)
    private readonly _getFactoryPsHttpSSE!: FactoryPsHttpSSE;


    get options() {
        return {
            headers: {
                'X-API-Version': '2.0',
            },
        };
    }

    /*
    * 该请求用于SSE接口刷新token使用，直接使用psHttp调用SSE接口，仍会发生长连接的问题
    */
    private _refreshToken() {
        return psHttp({
            url: `${accountServiceApiUrlPrefix}/account-info/extra-data`,
            method: 'get',
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }

    async translateSpecificField(data: ApiTranslateSpecificFieldRequest): Promise<ApiTranslateSpecificFieldResponse> {
        const res = await this.post<PatSnapSuccessResponseData<ApiTranslateSpecificFieldResponse>>({
            url: `${coreSearchApiUrlPrefix}/translate/specific-field`,
            data,
            transformCode: true,
        });
        return res.data;
    }

    async getSelectedLanguage(params: ApiSearchSettingLanguageRequest): Promise<ApiSearchSettingLanguageResponse> {
        const res = await this.get<PatSnapSuccessResponseData<ApiSearchSettingLanguageResponse>>({
            url: `${coreSearchApiUrlPrefix}/search-setting/language`,
            params,
            transformCode: true,
        });
        return res.data;
    }

    async fetchCompareInit(data: ApiFetchCompareInitRequest) {
        const res = await this.post<PatSnapSuccessResponseData<ApiFetchCompareInitResponse>>({
            url: `${coreSearchApiUrlPrefix}/view/compare/v2/compare-init`,
            data,
            transformCode: true,
        });
        return res.data;
    }

    async getCompareList(params: ApiGetCompareListRequest) {
        const res = await this.get<PatSnapSuccessResponseData<ApiGetCompareListResponse>>({
            url: `${coreSearchApiUrlPrefix}/view/compare/v2/compare-list`,
            params,
            transformCode: true,
        });
        return res.data;
    }

    getCompareOverviewChannel = Symbol('getCompareOverviewChannel');
    async getCompareOverview(data: ApiGetCompareOverviewRequest) {
        const res = await this.channel<PatSnapSuccessResponseData<ApiGetCompareOverviewResponse>>(
            this.getCompareOverviewChannel,
            {
                url: `${coreSearchApiUrlPrefix}/view/compare/v2/compare-overview`,
                method: 'POST',
                data,
                transformCode: true,
            },
        );
        return res.data;
    }

    async getCompareText(data: ApiGetCompareTextRequest) {
        const res = await this.post<PatSnapSuccessResponseData<ApiGetCompareTextResponse>>({
            url: `${coreSearchApiUrlPrefix}/view/compare/v2/compare-text`,
            data,
            transformCode: true,
        });
        return res.data;
    }

    async searchComparePatent(data: ApiSearchComparePatentRequest) {
        const res = await this.post<PatSnapSuccessResponseData<ApiSearchComparePatentResponse>>({
            url: `${coreSearchApiUrlPrefix}/view/compare/v2/compare-search`,
            data,
            transformCode: true,
        });
        return res.data;
    }

    async addComparePatent(data: ApiAddComparePatentRequest) {
        const res = await this.post<PatSnapSuccessResponseData<ApiAddComparePatentResponse>>({
            url: `${coreSearchApiUrlPrefix}/view/compare/v2/compare-add`,
            data,
            transformCode: true,
        });
        return res.data;
    }

    async getComparePatentText(data: ApiGetComparePatentTextRequest) {
        const res = await this.post<PatSnapSuccessResponseData<ApiGetComparePatentTextResponse>>({
            url: `${coreSearchApiUrlPrefix}/view/compare/v2/compare-patent`,
            data,
            transformCode: true,
            uuid: `compare-patent__${data.compare_id}_${data.patent_id}_${data.lang}_${data.field_type}`,
        });
        return res.data;
    }

    async searchSocketJob(data: ApiSearchSocketJobRequest): Promise<ApiSearchSocketJobResponse> {
        const res = await this.post<PatSnapSuccessResponseData<ApiSearchSocketJobResponse>>({
            url: `${coreSearchApiUrlPrefix}/copilot/job`,
            data,
            transformCode: true,
        });
        return res.data;
    }

    async getPatentInitTab(patentId: ApiGetPatentInitTabRequest): Promise<ApiGetPatentInitTabResponse> {
        const res = await this.get<PatSnapSuccessResponseData<ApiGetPatentInitTabResponse>>({
            url: `${coreSearchApiUrlPrefix}/patent/view/init-tab`,
            cache: true,
            uuid: `req_initTab_${patentId}`,
            params: {
                patent_id: patentId,
            },
        });
        return res.data;
    }

    async getPatentBasic(request: ApiGetPatentBasicRequest): Promise<ApiGetPatentBasicResponse> {
        const res = await this.post<PatSnapSuccessResponseData<ApiGetPatentBasicResponse>>({
            url: `${coreSearchApiUrlPrefix}/patent/id/${request.patentId}/basic`,
            cache: true,
            uuid: `basic_${request.patentId}_${JSON.stringify(request.params || {})}_${JSON.stringify(request.data || {})}`,
            params: request.params,
            data: request.data,
        });
        return res.data;
    }

    async getPatentValuation(patentId: ApiGetPatentValuationRequest): Promise<ApiGetPatentValuationResponse> {
        const res = await this.get<PatSnapSuccessResponseData<ApiGetPatentValuationResponse>>({
            url: `${coreSearchApiUrlPrefix}/patent/id/${patentId}/valuation`,
            transformCode: true,
        });
        return res.data;
    }

    async getPatentViewInit(params: ApiGetPatentViewInitRequest): Promise<ApiGetPatentViewInitResponse> {
        const res = await this.get<PatSnapSuccessResponseData<ApiGetPatentViewInitResponse>>({
            url: `${coreSearchApiUrlPrefix}/patent/view/init`,
            params,
        });
        return res.data;
    }

    getClaimFeature(params: ApiGetClaimFeatureRequest): PsHttpSSE<ApiGetClaimFeatureResponse> {
        return this._getFactoryPsHttpSSE<ApiGetClaimFeatureResponse>().init({
            url: `${ coreSearchApiUrlPrefix }/claim-compare/claim-separate?patent_id=${params.patent_id}`,
            method: 'GET',
        });
    }

    async getClaimCompareMatch(data: ApiGetClaimCompareMatchRequest): Promise<ApiGetClaimCompareMatchResponse> {
        const res = await this.post<PatSnapSuccessResponseData<ApiGetClaimCompareMatchResponse>>({
            url: `${ coreSearchApiUrlPrefix }/claim-compare/result/position`,
            data,
        });
        return res.data;
    }

    async getClaimCompareTranslation(data: ApiGetClaimCompareTranslationRequest): Promise<ApiGetClaimCompareTranslationResponse> {
        const res = await this.post<PatSnapSuccessResponseData<ApiGetClaimCompareTranslationResponse>>({
            url: `${ coreSearchApiUrlPrefix }/claim-compare/translation`,
            data,
            transformCode: true,
        });
        return res.data;
    }

    async setClaimCompareTranslationCache(data: ApiSetClaimCompareTranslationCacheRequest): Promise<ApiSetClaimCompareTranslationCacheResponse> {
        const res = await this.post<PatSnapSuccessResponseData<ApiSetClaimCompareTranslationCacheResponse>>({
            url: `${ coreSearchApiUrlPrefix }/claim-compare/translation/cache`,
            data,
            transformCode: true,
        });
        return res.data;
    }

    getClaimCompareResult(params: ApiGetClaimCompareResultRequest): PsHttpSSE<ApiGetClaimCompareResultResponse> {
        return this._getFactoryPsHttpSSE<ApiGetClaimCompareResultResponse>().init({
            url: `${ coreSearchApiUrlPrefix }/claim-compare/result`,
            data: params,
        });
    }

    async getPatentPum(params: ApiGetPatentPumRequest): Promise<ApiGetPatentPumResponse> {
        const res = await this.get<PatSnapSuccessResponseData<ApiGetPatentPumResponse>>({
            url: `${coreSearchApiUrlPrefix}/patent/id/${params.patentId}/pum`,
            transformCode: true,
            params: {
                lang: params.lang,
            },
        });
        return res.data;
    }
}
